import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Card,
  CircularProgress,
  Alert,
  Snackbar,
  TextField,
  MenuItem,
} from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

import { createTenant } from 'services/tenantService';

const validationSchema = Yup.object({
  businessName: Yup.string().required('Business name is required'),
  tenantName: Yup.string()
    .required('Short name is required')
    .matches(/^[a-zA-Z0-9-]+$/, 'Short name can only contain letters, numbers, and hyphens (no spaces)'),
  tenantEmail: Yup.string().email('Must be a valid email').required('Email is required'),
  tenantPhone: Yup.string().required('Phone is required'),
  tenantAddress: Yup.string(),
  trialDays: Yup.number().required('Trial days is required').positive().integer(),
  trialScans: Yup.number().required('Trial scans is required').positive().integer(),
  tenantTier: Yup.string()
    .oneOf(['Basic', 'Standard', 'Premium', 'Platinum'], 'Select a valid plan')
    .required('Plan is required'),
});

function TenantCreate() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const formik = useFormik({
    initialValues: {
      businessName: '',
      tenantName: '',
      tenantEmail: '',
      tenantPhone: '',
      tenantAddress: '',
      trialDays: 14,
      trialScans: 40,
      tenantTier: 'Standard',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        await createTenant(values);
        setSnackbar({ open: true, message: 'Tenant created successfully!', severity: 'success' });
        setTimeout(() => navigate('/tenants'), 1500);
      } catch (err) {
        console.error('Error creating tenant:', err);
        setSnackbar({ open: true, message: 'Failed to create tenant', severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox p={3}>
              <SoftTypography variant="h6" mb={2}>Provision Tenant</SoftTypography>
              <form onSubmit={formik.handleSubmit}>
                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold">Business Name *</SoftTypography>
                  <SoftInput
                    placeholder="Business Name"
                    name="businessName"
                    value={formik.values.businessName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.businessName && Boolean(formik.errors.businessName)}
                  />
                  {formik.touched.businessName && formik.errors.businessName && (
                    <SoftTypography variant="caption" color="error">{formik.errors.businessName}</SoftTypography>
                  )}
                </SoftBox>

                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold">Short Name *</SoftTypography>
                  <SoftInput
                    placeholder="tenant1"
                    name="tenantName"
                    value={formik.values.tenantName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.tenantName && Boolean(formik.errors.tenantName)}
                  />
                  {formik.touched.tenantName && formik.errors.tenantName && (
                    <SoftTypography variant="caption" color="error">{formik.errors.tenantName}</SoftTypography>
                  )}
                </SoftBox>

                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold">Email *</SoftTypography>
                  <SoftInput
                    placeholder="jsmith@example.com"
                    name="tenantEmail"
                    type="email"
                    value={formik.values.tenantEmail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.tenantEmail && Boolean(formik.errors.tenantEmail)}
                  />
                  {formik.touched.tenantEmail && formik.errors.tenantEmail && (
                    <SoftTypography variant="caption" color="error">{formik.errors.tenantEmail}</SoftTypography>
                  )}
                </SoftBox>

                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold">Phone *</SoftTypography>
                  <SoftInput
                    placeholder="1234567890"
                    name="tenantPhone"
                    value={formik.values.tenantPhone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.tenantPhone && Boolean(formik.errors.tenantPhone)}
                  />
                  {formik.touched.tenantPhone && formik.errors.tenantPhone && (
                    <SoftTypography variant="caption" color="error">{formik.errors.tenantPhone}</SoftTypography>
                  )}
                </SoftBox>

                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold">Address</SoftTypography>
                  <SoftInput
                    placeholder="Address"
                    name="tenantAddress"
                    value={formik.values.tenantAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </SoftBox>

                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold">Trial Days *</SoftTypography>
                  <SoftInput
                    placeholder="Trial Days"
                    name="trialDays"
                    type="number"
                    value={formik.values.trialDays}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.trialDays && Boolean(formik.errors.trialDays)}
                  />
                  {formik.touched.trialDays && formik.errors.trialDays && (
                    <SoftTypography variant="caption" color="error">{formik.errors.trialDays}</SoftTypography>
                  )}
                </SoftBox>

                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold">Trial Scans *</SoftTypography>
                  <SoftInput
                    placeholder="Trial Scans"
                    name="trialScans"
                    type="number"
                    value={formik.values.trialScans}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.trialScans && Boolean(formik.errors.trialScans)}
                  />
                  {formik.touched.trialScans && formik.errors.trialScans && (
                    <SoftTypography variant="caption" color="error">{formik.errors.trialScans}</SoftTypography>
                  )}
                </SoftBox>

                <SoftBox mb={3}>
                  <TextField
                    name="tenantTier"
                    label="Plan"
                    select
                    value={formik.values.tenantTier}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.tenantTier && Boolean(formik.errors.tenantTier)}
                    helperText={formik.touched.tenantTier && formik.errors.tenantTier ? formik.errors.tenantTier : 'Select a plan for this tenant'}
                    fullWidth
                    required
                  >
                    <MenuItem value="Basic">Basic</MenuItem>
                    <MenuItem value="Standard">Standard</MenuItem>
                    <MenuItem value="Premium">Premium</MenuItem>
                    <MenuItem value="Platinum">Platinum</MenuItem>
                  </TextField>
                </SoftBox>

                <SoftBox display="flex" gap={2}>
                  {!submitting ? (
                    <>
                      <SoftButton type="submit" variant="gradient" color="info" disabled={!formik.isValid}>
                        Submit
                      </SoftButton>
                      <SoftButton variant="gradient" color="secondary" onClick={() => navigate('/tenants')}>
                        Cancel
                      </SoftButton>
                    </>
                  ) : (
                    <CircularProgress size={24} />
                  )}
                </SoftBox>
              </form>
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default TenantCreate;
